import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { ISelectOption } from 'types/IClassifier'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import {
  ADDRESS_LINE_MAX_LENGTH,
  CL190_COUNTRY_ADDRESS_POSTCODE_BASED,
} from 'shared/ncts/common/constants'
import { removePrefix } from 'shared/ncts/common/classifier-util'
import useCodelist, { createDepartureCustomsOfficesBySupportedCountriesFilter } from 'features/declaration/codelist/api/useCodelist'
import E1102Validator from 'features/declaration/form/validations/E1102'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import Input from 'components/p5/Input'
import FormField from 'components/p5/FormField'
import FormSelect from 'components/p5/FormSelect'
import { getSelectCountryFilter } from 'components/react-select/CountryFunctions'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const scope = 'locationOfGoods' as const

const SK_QUALIFIERS_BY_TYPE_OF_LOCATION: Record<string, string[]> = {
  A: ['V'], B: ['Y'], C: ['Y', 'Z'], D: ['Z'],
}

function LocationOfGoods() {
  const { t } = useTranslation()
  const { getValues, watch, setValue } = useFormContext<TransitOperationFormValues>()

  const qualifierOfIdentification = watch(`${scope}.qualifierOfIdentification`)
  const office = getValues('departureCustomsOffice')
  const typeOfLocation = watch(`${scope}.typeOfLocation`)
  const authorisations = getValues('authorisations')

  const country = useMemo(() => office.slice('DEPARTURE_OFFICE_'.length, 'DEPARTURE_OFFICE_'.length + 2), [office])
  const departureCustomsOfficesFilterFn = useCallback(() => createDepartureCustomsOfficesBySupportedCountriesFilter(), [])
  const [, departureCustomsOffices] = useCodelist('DEPARTURE_CUSTOMS_OFFICES', departureCustomsOfficesFilterFn)
  const [, countriesOptions] = useCodelist('COUNTRIES')
  const [, qualifierOfIdentificationOptions] = useCodelist('QUALIFIER_OF_IDENTIFICATION')

  const departureOffices = useMemo(() => departureCustomsOffices.filter((item) => item.value.includes(`DEPARTURE_OFFICE_${country || 'FI'}`)), [departureCustomsOffices, country])
  const qualifierOfIdentifications = useMemo(() => {
    // EE CRS17
    if (country === 'EE' && (typeOfLocation === 'B' || typeOfLocation === 'C')) {
      return qualifierOfIdentificationOptions.filter((item) => item.value.includes('QUALIFIER_OF_IDENTIFICATION_Y'))
    }
    // SK NR014
    if (country === 'SK' && typeOfLocation) {
      const allowed = SK_QUALIFIERS_BY_TYPE_OF_LOCATION[typeOfLocation] ?? []
      return qualifierOfIdentificationOptions
        .filter((item) => allowed.some((qualifier) => item.value === `QUALIFIER_OF_IDENTIFICATION_${qualifier}`))
    }
    return qualifierOfIdentificationOptions
  }, [country, qualifierOfIdentificationOptions, typeOfLocation])

  const postcodeCountriesOptions = useMemo(
    () => countriesOptions.filter((countryOption) => CL190_COUNTRY_ADDRESS_POSTCODE_BASED.includes(removePrefix(countryOption.value, 'COUNTRY_') ?? '')),
    [countriesOptions],
  )

  const typeOfLocationOptions: ISelectOption[] = [
    {
      value: 'A',
      label: 'A - Designated location',
    },
    {
      value: 'B',
      label: 'B - Authorised place',
    },
    {
      value: 'C',
      label: 'C - Approved place',
    },
    {
      value: 'D',
      label: 'D - Other',
    },
  ].filter((option) => {
    if (country === 'FI' && authorisations?.find((auth) => auth.type === 'AUTHORISATION_TYPE_C521')) {
      return option.value === 'B' || option.value === 'C'
    }
    return option
  })

  // SK NR014: a qualifier the new type does not allow would otherwise stay set, hidden from the list.
  useEffect(() => {
    if (country !== 'SK' || !typeOfLocation || !qualifierOfIdentification) {
      return
    }
    const allowed = SK_QUALIFIERS_BY_TYPE_OF_LOCATION[typeOfLocation] ?? []
    if (!allowed.some((qualifier) => qualifierOfIdentification === `QUALIFIER_OF_IDENTIFICATION_${qualifier}`)) {
      setValue(`${scope}.qualifierOfIdentification`, '', { shouldDirty: true, shouldTouch: true })
    }
  }, [country, typeOfLocation, qualifierOfIdentification, setValue])

  useEffect(() => {
    if (qualifierOfIdentification !== 'QUALIFIER_OF_IDENTIFICATION_U') {
      setValue('locationOfGoods.unLocode', '', {
        shouldDirty: true,
        shouldTouch: true,
      })
    }
  }, [qualifierOfIdentification, setValue])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', [
    'locationOfGoods',
    'locationOfGoods.typeOfLocation',
    'locationOfGoods.unLocode',
    'locationOfGoods.qualifierOfIdentification',
    'locationOfGoods.additionalIdentifier',
    'locationOfGoods.economicOperatorIdentificationNumber',
    'locationOfGoods.authorisationNumber',
    'locationOfGoods.address',
    'locationOfGoods.address.addressLine',
    'locationOfGoods.address.country',
    'locationOfGoods.address.city',
    'locationOfGoods.address.houseNumber',
    'locationOfGoods.address.postcode',
    'locationOfGoods.referenceNumber',
    'locationOfGoods.postcodeAddress.country',
    'locationOfGoods.postcodeAddress.houseNumber',
    'locationOfGoods.postcodeAddress.postcode',
  ])

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-location-of-goods"
        headingLabel={t('declaration.p5.locationOfGoods')}
        initialIsSectionOpen={false}
        fieldRule={fieldRequirements.locationOfGoods}
      >
        <FormField
          labelKey="declaration.p5.typeOfLocation"
          tooltipContent={country === 'FI' ? (
            <small>
              {t('tooltips.typeOfLocation')}
            </small>
          ) : undefined}
          fieldRule={fieldRequirements['locationOfGoods.typeOfLocation']}
        >
          <FormSelect
            <TransitOperationFormValues>
            field="locationOfGoods.typeOfLocation"
            labelKey="declaration.p5.typeOfLocation"
            options={typeOfLocationOptions}
            type="sync"
            fieldRule={fieldRequirements['locationOfGoods.typeOfLocation']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.qualifierOfIdentification"
          fieldRule={fieldRequirements['locationOfGoods.qualifierOfIdentification']}
        >
          <FormSelect
            field="locationOfGoods.qualifierOfIdentification"
            labelKey="declaration.p5.qualifierOfIdentification"
            options={qualifierOfIdentifications}
            type="sync"
            fieldRule={fieldRequirements['locationOfGoods.qualifierOfIdentification']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.unLocode"
          fieldRule={fieldRequirements['locationOfGoods.unLocode']}
        >
          <Input
            field="locationOfGoods.unLocode"
            autoComplete={t('declaration.p5.unLocode')}
            labelKey="declaration.p5.unLocode"
            maxLength={17}
            type="text"
            placeholder={t('declaration.p5.unLocode')}
            fieldRule={fieldRequirements['locationOfGoods.unLocode']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.additionalIdentifier"
          fieldRule={fieldRequirements['locationOfGoods.additionalIdentifier']}
        >
          <Input
            field="locationOfGoods.additionalIdentifier"
            placeholder={t('declaration.p5.additionalIdentifier')}
            labelKey="declaration.p5.additionalIdentifier"
            fieldRule={fieldRequirements['locationOfGoods.additionalIdentifier']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.locationOfGoodsReferenceNumber"
          fieldRule={fieldRequirements['locationOfGoods.referenceNumber']}
        >
          <FormSelect
            field="locationOfGoods.referenceNumber"
            labelKey="declaration.p5.locationOfGoodsReferenceNumber"
            options={departureOffices}
            type="sync"
            fieldRule={fieldRequirements['locationOfGoods.referenceNumber']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.authorisationNumber"
          fieldRule={fieldRequirements['locationOfGoods.authorisationNumber']}
        >
          <Input
            field="locationOfGoods.authorisationNumber"
            placeholder={t('declaration.p5.authorisationNumber')}
            labelKey="declaration.p5.authorisationNumber"
            fieldRule={fieldRequirements['locationOfGoods.authorisationNumber']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.economicOperatorIdentificationNumber"
          tooltipContent={country === 'FI' ? (
            <small>
              {t('tooltips.warehouseEori')}
            </small>
          ) : undefined}
          fieldRule={fieldRequirements['locationOfGoods.economicOperatorIdentificationNumber']}
        >
          <Input
            field="locationOfGoods.economicOperatorIdentificationNumber"
            labelKey="declaration.p5.economicOperatorIdentificationNumber"
            maxLength={17}
            placeholder={t('declaration.p5.economicOperatorIdentificationNumber')}
            fieldRule={fieldRequirements['locationOfGoods.economicOperatorIdentificationNumber']}
          />

        </FormField>
        {
          qualifierOfIdentification !== 'QUALIFIER_OF_IDENTIFICATION_T'
            ? (
              <>
                <FormField
                  labelKey="declaration.p5.country"
                  fieldRule={fieldRequirements['locationOfGoods.address.country']}
                >
                  <FormSelect
                    field={`${scope}.address.country`}
                    labelKey="declaration.p5.country"
                    options={countriesOptions}
                    customFilterOption={getSelectCountryFilter}
                    type="sync"
                    fieldRule={fieldRequirements['locationOfGoods.address.country']}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.addressLine"
                  fieldRule={fieldRequirements['locationOfGoods.address.addressLine']}
                >
                  <Input
                    field={`${scope}.address.addressLine`}
                    autoComplete={`${scope}.address.addressLine`}
                    labelKey="declaration.p5.addressLine"
                    maxLength={ADDRESS_LINE_MAX_LENGTH}
                    type="text"
                    placeholder={t('declaration.p5.addressLine')}
                    fieldRule={fieldRequirements['locationOfGoods.address.addressLine']}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.city"
                  fieldRule={fieldRequirements['locationOfGoods.address.city']}
                >
                  <Input
                    field={`${scope}.address.city`}
                    autoComplete={`${scope}.address.city`}
                    labelKey="declaration.p5.city"
                    maxLength={35}
                    type="text"
                    placeholder={t('declaration.p5.city')}
                    fieldRule={fieldRequirements['locationOfGoods.address.city']}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.postcode"
                  fieldRule={fieldRequirements['locationOfGoods.address.postcode']}
                >
                  <Input
                    field={`${scope}.address.postcode`}
                    autoComplete={`${scope}.address.postcode`}
                    labelKey="declaration.p5.postcode"
                    maxLength={E1102Validator.getPostcodeMaxLength()}
                    type="text"
                    placeholder={t('declaration.p5.postcode')}
                    fieldRule={fieldRequirements['locationOfGoods.address.postcode']}
                  />
                </FormField>
              </>
            )
            : (
              <>
                <FormField
                  labelKey="declaration.p5.country"
                  fieldRule={fieldRequirements['locationOfGoods.postcodeAddress.country']}
                >
                  <FormSelect
                    field={`${scope}.postcodeAddress.country`}
                    labelKey="declaration.p5.country"
                    options={postcodeCountriesOptions}
                    customFilterOption={getSelectCountryFilter}
                    type="sync"
                    fieldRule={fieldRequirements['locationOfGoods.postcodeAddress.country']}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.houseNumber"
                  fieldRule={fieldRequirements['locationOfGoods.postcodeAddress.houseNumber']}
                >
                  <Input
                    field={`${scope}.postcodeAddress.houseNumber`}
                    autoComplete={`${scope}.postcodeAddress.houseNumber`}
                    labelKey="declaration.p5.houseNumber"
                    maxLength={17}
                    type="text"
                    placeholder={t('declaration.p5.houseNumber')}
                    fieldRule={fieldRequirements['locationOfGoods.postcodeAddress.houseNumber']}
                  />
                </FormField>
                <FormField
                  labelKey="declaration.p5.postcode"
                  fieldRule={fieldRequirements['locationOfGoods.postcodeAddress.postcode']}
                >
                  <Input
                    field={`${scope}.postcodeAddress.postcode`}
                    autoComplete={`${scope}.postcodeAddress.postcode`}
                    labelKey="declaration.p5.postcode"
                    maxLength={E1102Validator.getPostcodeMaxLength()}
                    type="text"
                    placeholder={t('declaration.p5.postcode')}
                    fieldRule={fieldRequirements['locationOfGoods.postcodeAddress.postcode']}
                  />
                </FormField>
              </>
            )
        }

      </FormCollapsibleColumn>
    </section>
  )
}

export default LocationOfGoods
